/**
 * ============================================================================
 *  logiqueInfos.ts — Ce que décide le menu d'informations, sans l'écran
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_credits/`. Les composants ne
 *  décident de rien ; tout ce qui se TESTE est ici, en fonctions pures :
 *
 *   - la SESSION (menu ouvert, crédits ouverts, voix mise en pause par nous) et
 *     l'effet qu'une transition a sur la narration ;
 *   - la navigation ↑ ↓ du menu ;
 *   - la décision de fermer la feuille au glisser.
 *
 *  La règle de la voix : elle ne se met en pause que si elle JOUAIT, et ne
 *  reprend que si c'est NOUS qui l'avons arrêtée, quand plus rien n'est ouvert.
 *  Un visiteur qui avait déjà fait pause ne la voit donc pas repartir seule.
 * ==========================================================================*/

export interface SessionInfos {
  menu: boolean;
  credits: boolean;
  /** La voix a été arrêtée par ce menu : c'est à lui de la relancer. */
  pause: boolean;
}

export const sessionInitiale: SessionInfos = { menu: false, credits: false, pause: false };

export type EvenementInfos =
  | { type: "basculerMenu" }
  | { type: "fermerMenu" }
  /** `garderMenu` : la feuille mobile reste dessous (« Retour » y revient) ; le menu desktop se ferme. */
  | { type: "ouvrirCredits"; garderMenu: boolean }
  /** « ‹ Retour » (mobile) : les crédits se ferment, la feuille reste. */
  | { type: "retourCredits" }
  /** × ou Échap : tout se ferme. */
  | { type: "fermerTout" }
  /** Le lien externe s'ouvre : la voix ne doit pas continuer derrière un autre onglet. */
  | { type: "lien" };

export type EffetNarration = "pause" | "reprise" | null;

export interface Transition {
  session: SessionInfos;
  effet: EffetNarration;
}

/** `jouait` : la voix est en cours de lecture à cet instant (guide ou main, pas en pause). */
export function transition(session: SessionInfos, evenement: EvenementInfos, jouait: boolean): Transition {
  let { menu, credits } = session;
  let veutPause = false;

  switch (evenement.type) {
    case "basculerMenu":
      menu = !menu;
      break;
    case "fermerMenu":
      menu = false;
      break;
    case "ouvrirCredits":
      credits = true;
      menu = evenement.garderMenu ? menu : false;
      veutPause = true;
      break;
    case "retourCredits":
      credits = false;
      break;
    case "fermerTout":
      menu = false;
      credits = false;
      break;
    case "lien":
      veutPause = true;
      break;
  }

  let pause = session.pause;
  let effet: EffetNarration = null;
  if (veutPause && jouait && !pause) {
    pause = true;
    effet = "pause";
  } else if (!menu && !credits && pause) {
    pause = false;
    effet = "reprise";
  }
  return { session: { menu, credits, pause }, effet };
}

/**
 * L'entrée à focaliser après une touche, dans un menu de `n` entrées. `courant` vaut -1 quand
 * aucune n'a le focus (↓ va à la première, ↑ à la dernière). `null` : la touche n'est pas du menu.
 */
export function indexMenu(courant: number, n: number, touche: string): number | null {
  if (n <= 0) return null;
  switch (touche) {
    case "ArrowDown":
      return courant < 0 ? 0 : (courant + 1) % n;
    case "ArrowUp":
      return courant < 0 ? n - 1 : (courant - 1 + n) % n;
    case "Home":
      return 0;
    case "End":
      return n - 1;
    default:
      return null;
  }
}

/**
 * Relâcher la feuille : elle se ferme si elle est descendue de plus d'un quart de sa hauteur,
 * ou si le geste était un coup vif vers le bas (même règle que la feuille de navigation).
 * `dy` en px, `duree` en ms.
 */
export function fermerAuGlisser(dy: number, duree: number, hauteur: number): boolean {
  if (dy <= 0) return false;
  const vitesse = dy / Math.max(1, duree);
  return dy > hauteur / 4 || (dy > 20 && vitesse > 0.5);
}

/** Le raccourci F ne s'applique pas dans un champ de saisie, ni avec un modificateur. */
export function raccourciPleinEcran(touche: string, modificateur: boolean, cibleEstSaisie: boolean): boolean {
  return (touche === "f" || touche === "F") && !modificateur && !cibleEstSaisie;
}
